import { useMemo, useState } from "react";

import {
  GLOBAL_DRIVE_CONNECTION_ID,
  globalDriveFileState,
  useGlobalDriveConnection,
  useGlobalDriveFiles,
} from "../api";
import type { GlobalDataFileCounts, GlobalDataRecord } from "../api";
import { RegisterBadge, RegistersForDocumentsProvider } from "../components/RegisterBadge";
import { SourceTextModal } from "../components/SourceTextModal";
import { removalReasonLabel } from "../components/removalReason";
import { describeFileCounts } from "../utils/describeFileCounts";
import { driveOpenUrl } from "../utils/driveOpenUrl";
import { formatBytes } from "../utils/formatBytes";
import { formatRelative } from "../utils/formatRelative";
import { GlobalDriveFileStatePill } from "./GlobalDriveFileStatePill";
import { GlobalDriveRetryLink } from "./GlobalDriveRetryLink";
import {
  buildDriveFilesTree,
  countTreeFiles,
  sortedChildren,
  sortedFiles,
  useDriveTreeOpenState,
} from "../utils/driveFilesTree";
import type { DriveFilesNode } from "../utils/driveFilesTree";
import styles from "./GlobalDataTab.module.css";
import kbStyles from "./GlobalKnowledgeBases.module.css";
import { useAgentLabel } from "../api/hooks/useStatefulAgents";
import folderStyles from "./GlobalDriveFolder.module.css";
import table from "./ProjectFilesRoute.module.css";
import { Chevron } from "../components/Chevron";

const COLUMNS = 5;
const indent = (depth: number) => ({ paddingLeft: depth * 20 });

interface RowActions {
  onViewExtraction: (file: GlobalDataRecord) => void;
  // Drive file id -> the agent that keeps it, for each client knowledge base.
  knowledgeBases: Map<string, string>;
}

const FileRow = ({
  file,
  depth,
  onViewExtraction,
  knowledgeBases,
}: RowActions & { file: GlobalDataRecord; depth: number }) => {
  const removed = globalDriveFileState(file) === "removed";
  const keptBy = file.drive_file_id ? knowledgeBases.get(file.drive_file_id) : undefined;
  return (
    <tr className={table.row}>
      <td className={table.td}>
        <div className={folderStyles.fileCell} style={indent(depth)}>
          {keptBy && <span className={kbStyles.kbBadge}>Client knowledge base · kept by {keptBy}</span>}
          {file.drive_file_id && !removed ? (
            <a
              className={folderStyles.driveLink}
              href={driveOpenUrl(file.drive_file_id)}
              target="_blank"
              rel="noreferrer"
              title={file.drive_path ?? undefined}
            >
              {file.original_name}
            </a>
          ) : (
            <span
              className={removed ? folderStyles.fileNameRemoved : table.fileName}
              title={file.drive_path ?? undefined}
            >
              {file.original_name}
            </span>
          )}
          {removed && file.removal_reason && (
            <span className={folderStyles.removedReason}>
              {removalReasonLabel(file.removal_reason)}
            </span>
          )}
          {file.summary && (
            <span className={folderStyles.fileSummary} title={file.summary}>
              {file.summary}
            </span>
          )}
        </div>
      </td>
      <td className={table.tdSmall}>
        <GlobalDriveFileStatePill record={file} />
      </td>
      <td className={table.tdSmall}>{formatBytes(file.size_bytes)}</td>
      <td className={table.tdSmall}>{formatRelative(file.updated_at)}</td>
      <td className={table.tdSmall}>
        <div className={folderStyles.fileActions}>
          {file.has_extracted_text && (
            <button type="button" className={styles.linkBtn} onClick={() => onViewExtraction(file)}>
              View extraction
            </button>
          )}
          <GlobalDriveRetryLink record={file} className={styles.linkBtn} />
          <RegisterBadge documentId={file.global_data_id} projectId={null} />
        </div>
      </td>
    </tr>
  );
};

interface SubtreeProps extends RowActions {
  node: DriveFilesNode<GlobalDataRecord>;
  depth: number;
  isOpen: (node: DriveFilesNode<GlobalDataRecord>, depth: number) => boolean;
  toggle: (key: string) => void;
}

const FolderSubtree = ({ node, depth, isOpen, toggle, ...actions }: SubtreeProps) => (
  <>
    {sortedChildren(node).map((child) => (
      <FolderRows
        key={child.key}
        node={child}
        depth={depth}
        isOpen={isOpen}
        toggle={toggle}
        {...actions}
      />
    ))}
    {sortedFiles(node).map((file) => (
      <FileRow key={file.global_data_id} file={file} depth={depth} {...actions} />
    ))}
  </>
);

const FolderRows = (props: SubtreeProps) => {
  const { node, depth, isOpen, toggle } = props;
  const open = isOpen(node, depth);
  return (
    <>
      <tr className={`${table.row} ${folderStyles.folderRow}`} onClick={() => toggle(node.key)}>
        <td className={table.td} colSpan={COLUMNS}>
          <div className={folderStyles.folderCell} style={indent(depth)}>
            <span><Chevron tree open={open} /></span>
            <span>📁</span>
            <span>
              {node.name}{" "}
              <span className={folderStyles.folderCount}>({countTreeFiles(node)})</span>
            </span>
          </div>
        </td>
      </tr>
      {open && <FolderSubtree {...props} depth={depth + 1} />}
    </>
  );
};

export const GlobalDriveFilesCard = ({ folders }: { folders: GlobalDataRecord[] }) => {
  const connection = useGlobalDriveConnection();
  const syncing =
    Boolean(connection.data?.active_sync_operation_key) ||
    folders.some((f) => f.active_sync_operation_key);
  const files = useGlobalDriveFiles({ poll: syncing });
  const { isOpen, toggle } = useDriveTreeOpenState();
  const { agentLabel } = useAgentLabel();
  const knowledgeBases = useMemo(
    () =>
      new Map(
        (connection.data?.files ?? []).flatMap((f) =>
          f.knowledge_base ? [[f.file_id, agentLabel(f.knowledge_base.kept_by)] as const] : [],
        ),
      ),
    [connection.data, agentLabel],
  );
  const [extractionFile, setExtractionFile] = useState<GlobalDataRecord | null>(null);

  const rows = useMemo(() => files.data ?? [], [files.data]);
  const groups = useMemo(
    () =>
      buildDriveFilesTree(rows, {
        groupIdOf: (file) => file.parent_global_data_id,
        trackedGroupId: GLOBAL_DRIVE_CONNECTION_ID,
        rootNames: new Map(folders.map((f) => [f.global_data_id, f.title])),
      }),
    [rows, folders],
  );
  const counts = useMemo(() => {
    const tally: GlobalDataFileCounts = { ready: 0, extracting: 0, failed: 0, removed: 0 };
    for (const file of rows) tally[globalDriveFileState(file)] += 1;
    return tally;
  }, [rows]);

  return (
    <RegistersForDocumentsProvider documentIds={rows.map((f) => f.global_data_id)}>
    <div className={folderStyles.filesCard}>
      <p className={styles.lede}>
        Synced from the connected shared drive on the 15-minute poll, grouped by global
        folder and their original folder structure. Configure the subscription in Google
        Drive Sync above.
      </p>
      {files.isLoading ? (
        <div className={styles.metaEmpty}>Loading synced files…</div>
      ) : files.isError ? (
        <div className={styles.error}>
          Couldn&apos;t load the synced files.{" "}
          <button type="button" className={styles.linkBtn} onClick={() => void files.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.metaEmpty}>
          {syncing
            ? "No files yet - a sync is still running."
            : "No files have synced from Drive yet."}
        </div>
      ) : (
        <>
          <div className={styles.dialogSub}>
            {describeFileCounts(counts)}
            {syncing && " · sync running"}
          </div>
          <div className={folderStyles.filesTable}>
            <table className={table.table}>
              <thead>
                <tr>
                  <th className={table.th}>Name</th>
                  <th className={table.th}>Status</th>
                  <th className={table.th}>Size</th>
                  <th className={table.th}>Updated</th>
                  <th className={table.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <FolderRows
                    key={group.key}
                    node={group}
                    depth={0}
                    isOpen={isOpen}
                    toggle={toggle}
                    onViewExtraction={setExtractionFile}
                    knowledgeBases={knowledgeBases}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {extractionFile && (
        <SourceTextModal
          globalDataId={extractionFile.global_data_id}
          hasSourceText={extractionFile.has_extracted_text}
          sourceLabel={extractionFile.original_name}
          contentType={extractionFile.content_type}
          onClose={() => setExtractionFile(null)}
        />
      )}
    </div>
    </RegistersForDocumentsProvider>
  );
};
